import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { style } from './styles';
import { Profile } from '@par/shared/types/player';
import { fetchPost } from '../api/common';
import { LocationsDropdown } from '../common/components/locationsDropdown';

type PropTypes = {
  onUpdateProfile: (updatedUsername?: string) => void;
  openModal: boolean;
  profile: Profile;
  setOpenModal: (open: boolean) => void;
};

export const EditProfileModal = ({
  onUpdateProfile,
  openModal,
  profile,
  setOpenModal,
}: PropTypes) => {
  const [editProfile, setEditProfile] = useState(profile);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!openModal) return;
    setEditProfile(profile);
    setError('');
  }, [openModal, profile]);

  const handleUsernameOnChange = (value: string) => {
    if (value.length > 40) return;
    setEditProfile((prev) => ({ ...prev, username: value }));
  };

  const handleFullNameOnChange = (value: string) => {
    setEditProfile((prev) => ({ ...prev, fullName: value }));
  };

  const handlePronounChange = (event: SelectChangeEvent) => {
    setEditProfile((prev) => ({ ...prev, genderPronoun: event.target.value }));
  };

  const handleSelectLocation = (
    location: string | null,
    locationPlaceId: string | null,
    locationLatitude: number | null,
    locationLongitude: number | null
  ) => {
    setEditProfile((prev) => ({
      ...prev,
      location,
      locationPlaceId,
      locationLatitude,
      locationLongitude,
    }));
  };

  const handleSubmit = async () => {
    if (!editProfile.username) {
      setError('Must provide a username');
      return;
    }
    try {
      await fetchPost('/profile/username', editProfile);
      onUpdateProfile(editProfile.username);
      setOpenModal(false);
    } catch (e: any) {
      const error = await e.json();
      if (error.message === 'Username already exists') {
        setError('username already in use');
        return;
      }
      throw Error('Profile update failed');
    }
  };

  return (
    <Modal
      open={openModal}
      onClose={() => setOpenModal(false)}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <Typography id="modal-modal-title" variant="h5" component="h2">
          Edit Your Profile
        </Typography>
        {error ? (
          <Typography color="error.main" mt={2}>
            {error}
          </Typography>
        ) : null}
        <Stack mb={2}>
          <TextField
            sx={{ mt: 2 }}
            id="username"
            label="Username"
            variant="standard"
            value={editProfile.username}
            onChange={(e) => handleUsernameOnChange(e.target.value)}
          />
          <TextField
            sx={{ mt: 2 }}
            id="fullname"
            label="Name (optional)"
            variant="standard"
            value={editProfile.fullName}
            onChange={(e) => handleFullNameOnChange(e.target.value)}
          />
        </Stack>
        <Box sx={{ mt: 2 }}>
          <LocationsDropdown
            location={editProfile?.location}
            onSelectLocation={handleSelectLocation}
          />
        </Box>
        <FormControl variant="standard" fullWidth sx={{ mt: 2 }}>
          <InputLabel>Pronoun</InputLabel>
          <Select
            value={editProfile.genderPronoun}
            onChange={handlePronounChange}
            label="Pronoun"
          >
            <MenuItem value={'He/Him'}>He/Him</MenuItem>
            <MenuItem value={'She/Her'}>She/Her</MenuItem>
            <MenuItem value={'They/Their'}>They/Them</MenuItem>
          </Select>
        </FormControl>
        <Stack direction={'row'} mt={4}>
          <Button variant="text" onClick={() => setOpenModal(false)} fullWidth>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} fullWidth>
            Submit
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};
